import { useCallback, useEffect, useMemo, useState } from 'react'
import type { ReportRow } from '../lib/types'
import { buildColumns, compareValues, getColumnMeta, rowMatchesFilter } from './columnMeta'
import { findCatalogItem } from './reportCatalog'

export type SortDir = 'asc' | 'desc'

export interface SortState {
  column: string
  direction: SortDir
}

const PAGE_SIZES = [50, 100, 250, 0] as const

function visKey(reportId: string): string {
  return `report-cols:${reportId}`
}

function loadVisibleColumns(reportId: string, all: string[]): Set<string> {
  try {
    const raw = sessionStorage.getItem(visKey(reportId))
    if (!raw) {
      return new Set(all.filter((c) => !getColumnMeta(c).hidden))
    }
    const parsed = JSON.parse(raw) as string[]
    return new Set(parsed.filter((c) => all.includes(c)))
  } catch {
    return new Set(all.filter((c) => !getColumnMeta(c).hidden))
  }
}

function saveVisibleColumns(reportId: string, cols: string[]): void {
  try {
    sessionStorage.setItem(visKey(reportId), JSON.stringify(cols))
  } catch { /* quota */ }
}

export function useReportTableState(reportId: string, rows: ReportRow[]) {
  const allColumns = useMemo(() => buildColumns(rows), [rows])
  const catalog = findCatalogItem(reportId)

  const [filter, setFilter] = useState('')
  const [dayFilter, setDayFilter] = useState<string>('all')
  const [sort, setSort] = useState<SortState | null>(
    catalog?.defaultSort ? { column: catalog.defaultSort.column, direction: catalog.defaultSort.direction } : null,
  )
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState<number>(100)
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [visibleColumns, setVisibleColumns] = useState<Set<string>>(() => new Set())

  useEffect(() => {
    setFilter('')
    setDayFilter('all')
    setSort(catalog?.defaultSort
      ? { column: catalog.defaultSort.column, direction: catalog.defaultSort.direction }
      : null)
    setPage(0)
    setSelected(new Set())
    if (allColumns.length) {
      setVisibleColumns(loadVisibleColumns(reportId, allColumns))
    } else {
      setVisibleColumns(new Set())
    }
  }, [reportId, catalog?.defaultSort, allColumns.length])

  const columns = useMemo(
    () => allColumns.filter((c) => visibleColumns.has(c)),
    [allColumns, visibleColumns],
  )

  const dayOptions = useMemo(() => {
    const col = allColumns.find((c) => c === 'day' || c === 'month')
    if (!col) return []
    const vals = new Set<string>()
    for (const r of rows) {
      const v = r[col]
      if (v != null) vals.add(String(v))
    }
    return [...vals].sort().reverse().slice(0, 5)
  }, [rows, allColumns])

  const filtered = useMemo(() => {
    let out = rows
    if (dayFilter !== 'all') {
      const col = allColumns.find((c) => c === 'day' || c === 'month')
      if (col) out = out.filter((r) => String(r[col] ?? '') === dayFilter)
    }
    if (filter.trim()) {
      out = out.filter((r) => rowMatchesFilter(r, filter, columns.length ? columns : allColumns))
    }
    return out
  }, [rows, filter, dayFilter, columns, allColumns])

  const sorted = useMemo(() => {
    if (!sort) return filtered
    const { column, direction } = sort
    const mul = direction === 'asc' ? 1 : -1
    return [...filtered].sort((a, b) => mul * compareValues(a[column], b[column], column))
  }, [filtered, sort])

  const totalPages = pageSize > 0 ? Math.max(1, Math.ceil(sorted.length / pageSize)) : 1
  const safePage = Math.min(page, totalPages - 1)

  const pageRows = useMemo(() => {
    if (pageSize <= 0) return sorted
    const start = safePage * pageSize
    return sorted.slice(start, start + pageSize)
  }, [sorted, safePage, pageSize])

  const toggleSort = useCallback((col: string) => {
    setSort((prev) => {
      if (!prev || prev.column !== col) return { column: col, direction: 'asc' }
      if (prev.direction === 'asc') return { column: col, direction: 'desc' }
      return null
    })
    setPage(0)
  }, [])

  const toggleColumn = useCallback((col: string) => {
    setVisibleColumns((prev) => {
      const next = new Set(prev)
      if (next.has(col)) {
        if (next.size <= 1) return prev
        next.delete(col)
      } else {
        next.add(col)
      }
      saveVisibleColumns(reportId, [...next])
      return next
    })
  }, [reportId])

  const toggleRow = useCallback((index: number, shift?: boolean) => {
    setSelected((prev) => {
      const next = shift ? new Set(prev) : new Set<number>()
      if (next.has(index)) next.delete(index)
      else next.add(index)
      return next
    })
  }, [])

  const selectAllPage = useCallback(() => {
    setSelected(new Set(pageRows.map((_, i) => safePage * (pageSize || sorted.length) + i)))
  }, [pageRows, safePage, pageSize, sorted.length])

  const clearSelection = useCallback(() => setSelected(new Set()), [])

  const selectedRows = useMemo(() => {
    if (!selected.size) return []
    return [...selected].map((i) => sorted[i]).filter(Boolean) as ReportRow[]
  }, [selected, sorted])

  return {
    filter, setFilter,
    dayFilter, setDayFilter, dayOptions,
    sort, toggleSort,
    page: safePage, setPage,
    pageSize, setPageSize,
    pageSizes: PAGE_SIZES,
    totalPages,
    columns, allColumns,
    visibleColumns, toggleColumn,
    filtered, sorted, pageRows,
    selected, toggleRow, selectAllPage, clearSelection, selectedRows,
    showingFrom: sorted.length ? safePage * (pageSize || sorted.length) + 1 : 0,
    showingTo: pageSize > 0
      ? Math.min((safePage + 1) * pageSize, sorted.length)
      : sorted.length,
    totalCount: sorted.length,
  }
}
